import React, { useEffect, useRef } from 'react';
import { Loading } from 'carbon-components-react';
import { loadStripe } from '@stripe/stripe-js';
import {
  MemberLookupPaymentStatus,
  LivePrice
} from 'redux/bydaMemberLookup/types';
import {
  PaymentScreenLayout,
  PaymentScreenLayoutSkeleton
} from 'components/PremiumWizard';
import {
  buildMemberLookupInvoicePricing,
  MEMBER_LOOKUP_INVOICE_BANNER
} from '../utils/buildMemberLookupInvoicePricing';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

const PAYMENT_STEP_SKELETON_LINE_ITEMS = [
  {
    label: 'Member contact list extraction',
    isHighlighted: true
  }
];

interface PaymentStepProps {
  paymentStatus: MemberLookupPaymentStatus;
  livePrice: LivePrice | null;
  isLivePriceLoading: boolean;
  clientSecret: string | null;
  purchaseOrderNumber: string;
  onPurchaseOrderNumberChange: (value: string) => void;
}

const StripeCheckoutEmbed: React.FC<{ clientSecret: string }> = ({
  clientSecret
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let checkout: {
      mount: (el: HTMLElement) => void;
      destroy: () => void;
    } | null = null;

    async function mountCheckout() {
      const stripe = await stripePromise;
      if (!stripe || !containerRef.current) return;

      const createEmbeddedCheckoutPage = stripe['createEmbeddedCheckoutPage'];
      if (typeof createEmbeddedCheckoutPage !== 'function') return;

      const instance = await createEmbeddedCheckoutPage({ clientSecret });
      checkout = instance;
      if (instance && containerRef.current) {
        instance.mount(containerRef.current);
      }
    }

    mountCheckout();

    return () => {
      checkout?.destroy();
    };
  }, [clientSecret]);

  return (
    <div
      ref={containerRef}
      className="byda-member-lookup__payment-step-stripe-embed"
    />
  );
};

const PaymentStep: React.FC<PaymentStepProps> = ({
  paymentStatus,
  livePrice,
  isLivePriceLoading,
  clientSecret,
  purchaseOrderNumber,
  onPurchaseOrderNumberChange
}) => {
  if (paymentStatus === 'creating' || paymentStatus === 'confirming') {
    return (
      <div className="byda-member-lookup__extraction-step">
        <Loading withOverlay={false} description="Processing" />
        <div className="byda-member-lookup__extraction-step__status-text">
          Preparing payment...
        </div>
      </div>
    );
  }

  if (paymentStatus === 'checkout' && clientSecret) {
    return (
      <div className="byda-member-lookup__payment-step-container">
        {/* <div className="byda-member-lookup__step-header">Payment</div> */}
        <StripeCheckoutEmbed clientSecret={clientSecret} />
      </div>
    );
  }

  if (paymentStatus === 'invoice') {
    const invoicePricing = livePrice
      ? buildMemberLookupInvoicePricing(livePrice)
      : null;

    return (
      <div>
        <div className="byda-member-lookup__step-header">Invoice</div>

        {isLivePriceLoading ? (
          <PaymentScreenLayoutSkeleton
            bannerMessage={MEMBER_LOOKUP_INVOICE_BANNER}
            lineItems={PAYMENT_STEP_SKELETON_LINE_ITEMS}
          />
        ) : (
          invoicePricing && (
            <PaymentScreenLayout
              bannerMessage={invoicePricing.bannerMessage}
              stripeAmount={invoicePricing.stripeAmount}
              stripeCurrency={invoicePricing.stripeCurrency}
              stripeDescription={invoicePricing.stripeDescription}
              lineItems={invoicePricing.lineItems}
              purchaseOrderNumber={purchaseOrderNumber}
              onPurchaseOrderNumberChange={onPurchaseOrderNumberChange}
              total={invoicePricing.total}
            />
          )
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="byda-member-lookup__step-header">Payment</div>
    </div>
  );
};

export default PaymentStep;
